import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, PencilLineIcon } from 'lucide-react';
import { DropdownPanel } from './DropdownPanel';
import { useDismiss } from '../hooks/useDismiss';
type CityComboboxProps = {
  id: string;
  value: string;
  options: string[];
  placeholder?: string;
  invalid?: boolean;
  onChange: (value: string) => void;
};

// A city field: pick from the list, or simply type your own
export function CityCombobox({
  id,
  value,
  options,
  placeholder,
  invalid,
  onChange
}: CityComboboxProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const listId = useId();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  const query = value.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!query || options.some(o => o.toLowerCase() === query)) return options;
    return options.filter(o => o.toLowerCase().includes(query));
  }, [options, query]);
  const isCustom = query.length > 1 && !options.some(o => o.toLowerCase() === query);
  const pick = (city: string) => {
    onChange(city);
    setOpen(false);
    setActive(-1);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive(a => Math.min(a + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      if (active >= 0 && matches[active]) pick(matches[active]);else setOpen(false);
    }
  };
  return <div ref={ref} className="relative" data-id="element-181" data-mp="13t2shtc7qe1e">
      <div className="flex items-end border-b border-ink" data-id="element-182" data-mp="3jmbj7c5pvlh0">
        <input id={id} role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list" aria-invalid={invalid} autoComplete="address-level2" value={value} onChange={e => {
        onChange(e.target.value);
        setOpen(true);
        setActive(-1);
      }} onFocus={() => setOpen(true)} onKeyDown={onKeyDown} placeholder={placeholder} className="min-w-0 flex-1 bg-transparent pb-3 pt-1.5 text-[18px] placeholder:font-light placeholder:text-ink/50 focus:outline-none" data-id="element-183" data-mp="3jo4p2p8ia1ny" />
        <button type="button" tabIndex={-1} aria-label="Show cities" onClick={() => setOpen(o => !o)} className="mb-3 p-1 text-ink/60 transition-colors duration-200 hover:text-ink" data-id="element-184" data-mp="16f2eecgw4b59">
          <ChevronDownIcon className={`h-4 w-4 transition-transform duration-300 ease-luxe ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-185" data-mp="xgglbndsldq8" />
        </button>
      </div>

      <DropdownPanel open={open} className="left-0 right-0" data-id="element-186" data-mp="dl5ljwajh945">
        <ul id={listId} role="listbox" className="max-h-[280px] overflow-y-auto py-2" data-id="element-187" data-mp="dkngqb9t9mkm">
          {matches.map((city, i) => <li key={city} role="option" aria-selected={value === city} data-id="element-188" data-mp="nap4f1y952z0" data-mpi={i}>
              <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => pick(city)} className={`flex w-full items-center gap-3 px-5 py-3 text-left text-[15px] transition-colors duration-200 hover:bg-bone ${i === active ? 'bg-bone' : ''} ${value === city ? 'font-medium' : 'font-light'}`} data-id="element-189" data-mp="nbid48gru2js">
                <span className="flex-1" data-id="element-190" data-mp="8m2qn4o9wj9w">{city}</span>
                {value === city && <CheckIcon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-191" data-mp="1f1wn3tlnyuq6" />}
              </button>
            </li>)}
          {matches.length === 0 && !isCustom && <li className="px-5 py-3 text-[15px] font-light text-ink/60" data-id="element-192" data-mp="1k2sr0760mgvl">No match</li>}
        </ul>
        <div className="flex items-center gap-2 border-t border-line px-5 py-3 text-[13px] font-light text-ink/65" data-id="element-193" data-mp="1ilwuxvsw1r94">
          <PencilLineIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-194" data-mp="1rkijq8oye2ic" />
          {isCustom ? <button type="button" onClick={() => pick(value.trim())} className="text-left text-ink hover:underline" data-id="element-195" data-mp="1ninit5axpr53">
              Use “{value.trim()}”
            </button> : <span data-id="element-196" data-mp="1wk3hwgwau6u0">Not listed? Simply type your own.</span>}
        </div>
      </DropdownPanel>
    </div>;
}